import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  getGetAiTaskQueryKey,
  getListNotificationsQueryKey,
  listNotifications,
  markAllNotificationsRead as requestMarkAllNotificationsRead,
  markAiTaskViewed,
  markNotificationRead,
} from "@workspace/api-client-react";
import type { AiTaskTarget } from "@workspace/api-client-react";
import {
  notificationListResponseSchema,
  toastClaimsResponseSchema,
  type NotificationItemRuntime,
  type NotificationListResponseRuntime,
} from "@workspace/api-zod";
import { toast } from "sonner";
import { invalidateNotificationTarget } from "./query-invalidation";
import { useAdActivity, type AdsAdapter } from "./ads";
import { consumeNotificationStream, StreamHttpError, type StreamTransportOptions } from "./stream-transport";
import { aiTaskTargetPath } from "@/ai-results/task-target";

export { consumeNotificationStream } from "./stream-transport";
export { StreamHttpError } from "./stream-transport";

const CLAIM_WINDOW_MS = 5 * 60 * 1000;
const MAX_SEEN_SIGNATURES = 512;
const NOTIFICATION_FALLBACK_MS = 30_000;
const TASK_FALLBACK_MS = 5_000;
const DEFAULT_STREAM: Omit<StreamTransportOptions, "signal" | "onSnapshot" | "getAuthToken"> = {
  mode: "fetch",
  url: "/api/notifications/stream",
  credentials: "same-origin",
};

export type ActivitySession = { key: string | null };
export type ActivityRuntimeAdapter = {
  useSession: () => ActivitySession;
  getAuthToken?: () => string | null | Promise<string | null>;
  onUnauthorized?: () => void;
  stream?: Omit<StreamTransportOptions, "signal" | "onSnapshot" | "getAuthToken">;
};

type ActivityContextValue = {
  streamConnected: boolean;
  reportResultRendered: (input: { taskId: string; target?: AiTaskTarget | null }) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
};
const ActivityContext = createContext<ActivityContextValue | null>(null);

type ClaimState = {
  id: number;
  generation: number;
  controller: AbortController;
  promise: Promise<void>;
};

function notificationSignature(item: NotificationItemRuntime): string {
  // Read/toast state is updated directly from every snapshot; it must not
  // retrigger the feature invalidation fan-out for the same task.
  return `${item.id}:${item.taskId}:${item.kind}`;
}

function trimSet(set: Set<string>, max: number): void {
  while (set.size > max) set.delete(set.values().next().value!);
}

function removeNotificationQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === "/api/notifications" });
}

export function streamBackoff(attempt: number, random = Math.random): number {
  const base = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
  return Math.min(30_000, Math.round(base * (0.8 + random() * 0.4)));
}

export function ActivityProvider({ runtime, adsAdapter, children }: {
  runtime: ActivityRuntimeAdapter;
  adsAdapter?: AdsAdapter;
  children: React.ReactNode;
}) {
  const adsReset = useMemo(() => () => adsAdapter?.resetSession?.(), [adsAdapter]);
  return <ActivityCoordinator runtime={runtime} adsReset={adsReset}>{children}</ActivityCoordinator>;
}

function ActivityCoordinator({ runtime, adsReset, children }: { runtime: ActivityRuntimeAdapter; adsReset?: () => void; children: React.ReactNode }) {
  const session = runtime.useSession();
  const queryClient = useQueryClient();
  const ads = useAdActivity();
  const [, wouterNavigate] = useLocation();
  const [streamConnected, setStreamConnected] = useState(false);
  const generation = useRef(0);
  const attemptSerial = useRef(0);
  const activeAttempt = useRef(0);
  const seenSignatures = useRef(new Set<string>());
  const claim = useRef<ClaimState | null>(null);
  const claimSequence = useRef(0);
  const claimAgain = useRef(false);
  const unauthorizedClaimGeneration = useRef<number | null>(null);
  const mountedAt = useRef(Date.now());
  const visible = useDocumentVisible();
  const adOpenRef = useRef(false);
  const processSnapshotRef = useRef<(raw: unknown, currentGeneration: number, attemptId: number | null) => Promise<void>>(async () => undefined);
  const activityToastIds = useRef(new Set<string | number>());
  const activeToastId = useRef<string | number | null>(null);
  const activeToastBatch = useRef<NotificationItemRuntime[]>([]);
  const pendingToastBatch = useRef<NotificationItemRuntime[]>([]);
  const showNextToastRef = useRef<() => void>(() => undefined);
  const lastReadAllAt = useRef<string | null>(null);

  const clearActivityToasts = useCallback(() => {
    const ids = [...activityToastIds.current];
    activityToastIds.current.clear();
    activeToastId.current = null;
    activeToastBatch.current = [];
    pendingToastBatch.current = [];
    // Clear coordinator state before dismissing. Sonner may synchronously run
    // onDismiss, which must not drain a previous session's pending batch.
    for (const id of ids) toast.dismiss(id);
  }, []);

  const showNextToast = useCallback(() => {
    if (activeToastId.current !== null || pendingToastBatch.current.length === 0) return;
    const batch = pendingToastBatch.current.splice(0);
    const single = batch.length === 1;
    const allFailed = batch.every((item) => item.kind === "ai-task-failed");
    const allSucceeded = batch.every((item) => item.kind === "ai-task-succeeded");
    const finish = (id: string | number) => {
      if (activeToastId.current !== id) return;
      activeToastId.current = null;
      activeToastBatch.current = [];
      activityToastIds.current.delete(id);
      showNextToast();
    };
    let id: string | number;
    const options = {
      onDismiss: () => finish(id),
      onAutoClose: () => finish(id),
      ...(single
        ? { description: batch[0].message, action: { label: "확인하기", onClick: () => wouterNavigate(aiTaskTargetPath(batch[0].target, batch[0].taskId)) } }
        : { action: { label: "알림 보기", onClick: () => wouterNavigate("/ai-results") } }),
    } as Parameters<typeof toast.success>[1];
    if (single && allSucceeded) id = toast.success("AI 작업이 완료됐어요", options);
    else if (single && allFailed) id = toast.error("AI 작업이 완료되지 않았어요", options);
    else if (!single && allSucceeded) id = toast.success(`AI 작업 ${batch.length}건이 완료됐어요`, options);
    else if (!single && allFailed) id = toast.error(`AI 작업 ${batch.length}건이 완료되지 않았어요`, options);
    else id = toast.info(`AI 작업 ${batch.length}건의 결과가 도착했어요`, options);
    activeToastId.current = id;
    activeToastBatch.current = batch;
    activityToastIds.current.add(id);
  }, [wouterNavigate]);
  showNextToastRef.current = showNextToast;

  const drainClaims = useCallback(async (currentGeneration: number) => {
    if (adOpenRef.current || currentGeneration !== generation.current) {
      if (adOpenRef.current) claimAgain.current = true;
      return;
    }
    if (claim.current) {
      claimAgain.current = true;
      return;
    }
    const controller = new AbortController();
    const id = ++claimSequence.current;
    const state = { id, generation: currentGeneration, controller, promise: Promise.resolve() } as ClaimState;
    claim.current = state;
    state.promise = Promise.resolve().then(async () => {
      try {
        const response = await fetch("/api/notifications/toast-claims", {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ limit: 20, eligibleAfter: new Date(mountedAt.current - CLAIM_WINDOW_MS).toISOString() }),
          signal: controller.signal,
        });
        if (response.status === 401) {
          if (unauthorizedClaimGeneration.current !== currentGeneration) {
            unauthorizedClaimGeneration.current = currentGeneration;
            runtime.onUnauthorized?.();
          }
          return;
        }
        if (!response.ok) return;
        const parsed = toastClaimsResponseSchema.safeParse(await response.json());
        if (!parsed.success || currentGeneration !== generation.current || claim.current?.id !== id) return;
        const claimedItems = parsed.data.items;
        queryClient.setQueryData<NotificationListResponseRuntime | undefined>(getListNotificationsQueryKey(), (current) => current ? {
          ...current,
          items: current.items.map((item) => claimedItems.find((claimed) => claimed.id === item.id) ?? item),
        } : current);
        pendingToastBatch.current.push(...claimedItems.filter((item) => !pendingToastBatch.current.some((pending) => pending.id === item.id)));
        if (!adOpenRef.current) {
          showNextToast();
        } else claimAgain.current = true;
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error instanceof StreamHttpError && error.status === 401 && unauthorizedClaimGeneration.current !== currentGeneration) {
          unauthorizedClaimGeneration.current = currentGeneration;
          runtime.onUnauthorized?.();
        }
      } finally {
        if (claim.current?.id === id) claim.current = null;
      }
    });
    await state.promise;
    if (claimAgain.current && !adOpenRef.current && currentGeneration === generation.current) {
      claimAgain.current = false;
      await drainClaims(currentGeneration);
    }
  }, [queryClient, runtime, showNextToast]);

  const processSnapshot = useCallback((raw: unknown, currentGeneration: number, attemptId: number | null): Promise<void> => {
    const parsed = notificationListResponseSchema.safeParse(raw);
    if (!parsed.success || currentGeneration !== generation.current || (attemptId !== null && attemptId !== activeAttempt.current)) {
      throw new Error("Malformed or stale notification snapshot.");
    }
    const readAllAt = lastReadAllAt.current;
    const staleBeforeReadAll = readAllAt !== null
      && Date.parse(parsed.data.serverTime) < Date.parse(readAllAt);
    const snapshot = staleBeforeReadAll ? {
      ...parsed.data,
      unreadCount: 0,
      items: parsed.data.items.map((item) => item.readAt ? item : { ...item, readAt: readAllAt }),
    } : parsed.data;
    queryClient.setQueryData(getListNotificationsQueryKey(), snapshot);
    for (const item of snapshot.items) {
      const signature = notificationSignature(item);
      if (seenSignatures.current.has(signature)) continue;
      seenSignatures.current.add(signature);
      trimSet(seenSignatures.current, MAX_SEEN_SIGNATURES);
      invalidateNotificationTarget(queryClient, item.target, item.taskId);
    }
    pendingToastBatch.current = pendingToastBatch.current.filter((pending) => snapshot.items.some((item) => item.id === pending.id && !item.readAt));
    const readIds = new Set(snapshot.items.filter((item) => item.readAt).map((item) => item.id));
    if (activeToastBatch.current.some((item) => readIds.has(item.id))) {
      const remaining = activeToastBatch.current.filter((item) => !readIds.has(item.id));
      const id = activeToastId.current;
      activeToastId.current = null;
      activeToastBatch.current = [];
      if (id !== null) {
        activityToastIds.current.delete(id);
        toast.dismiss(id);
      }
      const pendingIds = new Set(pendingToastBatch.current.map((item) => item.id));
      pendingToastBatch.current.unshift(...remaining.filter((item) => !pendingIds.has(item.id)));
      showNextToastRef.current();
    }
    if (snapshot.unreadCount === 0) {
      claim.current?.controller.abort();
      claim.current = null;
      claimAgain.current = false;
      pendingToastBatch.current = [];
      clearActivityToasts();
      return Promise.resolve();
    }
    if (!adOpenRef.current) {
      void drainClaims(currentGeneration);
      return Promise.resolve();
    }
    claimAgain.current = true;
    return Promise.resolve();
  }, [clearActivityToasts, drainClaims, queryClient]);
  processSnapshotRef.current = processSnapshot;
  adOpenRef.current = ads.isAnyAdOpen;

  useEffect(() => {
    const currentGeneration = ++generation.current;
    mountedAt.current = Date.now();
    seenSignatures.current.clear();
    pendingToastBatch.current = [];
    claimAgain.current = false;
    unauthorizedClaimGeneration.current = null;
    lastReadAllAt.current = null;
    adsReset?.();
    ads.resetAdActivity();
    setStreamConnected(false);
    return () => {
      generation.current += 1;
      activeAttempt.current = ++attemptSerial.current;
      claim.current?.controller.abort();
      claim.current = null;
      claimAgain.current = false;
      seenSignatures.current.clear();
      pendingToastBatch.current = [];
      lastReadAllAt.current = null;
      removeNotificationQueries(queryClient);
      clearActivityToasts();
      adsReset?.();
      ads.resetAdActivity();
      setStreamConnected(false);
      void currentGeneration;
    };
  }, [adsReset, clearActivityToasts, queryClient, runtime, session.key]);

  useEffect(() => {
    if (!session.key || !visible) return;
    const currentGeneration = generation.current;
    const loopController = new AbortController();
    let stopped = false;
    let attempt = 0;
    let timer: number | null = null;
    const schedule = (delay: number) => {
      if (stopped || loopController.signal.aborted) return;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => { timer = null; void connect(); }, delay);
    };
    const connect = async () => {
      if (stopped || loopController.signal.aborted || currentGeneration !== generation.current) return;
      const attemptId = ++attemptSerial.current;
      activeAttempt.current = attemptId;
      const attemptController = new AbortController();
      let malformedSnapshot = false;
      const relayAbort = () => attemptController.abort();
      loopController.signal.addEventListener("abort", relayAbort, { once: true });
      try {
        await consumeNotificationStream({
          ...(runtime.stream ?? DEFAULT_STREAM),
          signal: attemptController.signal,
          getAuthToken: runtime.getAuthToken,
          onSnapshot: (raw) => {
            if (attemptId !== activeAttempt.current || attemptController.signal.aborted) return;
            const parsed = notificationListResponseSchema.safeParse(raw);
            if (!parsed.success) {
              malformedSnapshot = true;
              attemptController.abort();
              return;
            }
            try {
              void processSnapshotRef.current(parsed.data, currentGeneration, attemptId)
                .then(() => {
                  if (!stopped && !attemptController.signal.aborted && attemptId === activeAttempt.current) {
                    attempt = 0;
                    setStreamConnected(true);
                  }
                })
                .catch(() => {
                  malformedSnapshot = true;
                  attemptController.abort();
                });
            } catch {
              malformedSnapshot = true;
              attemptController.abort();
            }
          },
        });
        if (malformedSnapshot) {
          setStreamConnected(false);
          schedule(streamBackoff(attempt));
          attempt += 1;
          return;
        }
        if (!stopped && !loopController.signal.aborted && attemptId === activeAttempt.current) {
          setStreamConnected(false);
          schedule(1_000);
        }
      } catch (error) {
        if (stopped || loopController.signal.aborted || (attemptController.signal.aborted && !malformedSnapshot)) return;
        setStreamConnected(false);
        if (error instanceof StreamHttpError && error.status === 401) {
          runtime.onUnauthorized?.();
          loopController.abort();
          return;
        }
        schedule(streamBackoff(attempt));
        attempt += 1;
      } finally {
        loopController.signal.removeEventListener("abort", relayAbort);
        attemptController.abort();
      }
    };
    void connect();
    return () => {
      stopped = true;
      activeAttempt.current = ++attemptSerial.current;
      loopController.abort();
      if (timer !== null) window.clearTimeout(timer);
      setStreamConnected(false);
    };
  }, [runtime, session.key, visible]);

  useEffect(() => {
    if (!session.key || !visible || streamConnected) return;
    const currentGeneration = generation.current;
    const controller = new AbortController();
    const runFallback = async () => {
      try {
        const snapshot = await listNotifications(undefined, { signal: controller.signal });
        await processSnapshotRef.current(snapshot, currentGeneration, null);
      } catch (error) {
        if (controller.signal.aborted || currentGeneration !== generation.current) return;
        if (httpStatus(error) === 401) runtime.onUnauthorized?.();
      }
    };
    const timer = window.setInterval(() => { void runFallback(); }, NOTIFICATION_FALLBACK_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [runtime, session.key, streamConnected, visible]);

  useEffect(() => {
    if (!ads.isAnyAdOpen && claimAgain.current && session.key) {
      claimAgain.current = false;
      void drainClaims(generation.current);
    }
  }, [ads.isAnyAdOpen, drainClaims, session.key]);

  const reportResultRendered = useCallback(async ({ taskId }: { taskId: string; target?: AiTaskTarget | null }) => {
    if (!session.key) return;
    const notificationIds = findNotificationIds(queryClient, taskId);
    pendingToastBatch.current = pendingToastBatch.current.filter((item) => item.taskId !== taskId);
    if (activeToastBatch.current.some((item) => item.taskId === taskId)) {
      const remaining = activeToastBatch.current.filter((item) => item.taskId !== taskId);
      const id = activeToastId.current;
      activeToastId.current = null;
      activeToastBatch.current = [];
      if (id !== null) {
        activityToastIds.current.delete(id);
        toast.dismiss(id);
      }
      const pendingIds = new Set(pendingToastBatch.current.map((item) => item.id));
      pendingToastBatch.current.unshift(...remaining.filter((item) => !pendingIds.has(item.id)));
      showNextToastRef.current();
    }
    const viewed = await Promise.allSettled([markAiTaskViewed(taskId)]);
    const reads = await Promise.all(notificationIds.map(async (id) => ({
      id,
      result: await Promise.allSettled([markNotificationRead(id)]),
    })));
    if (viewed[0].status === "fulfilled") void queryClient.invalidateQueries({ queryKey: getGetAiTaskQueryKey(taskId) });
    const readAtById = new Map<string, string>();
    for (const read of reads) {
      const result = read.result[0];
      if (result.status === "fulfilled") readAtById.set(read.id, result.value.readAt);
    }
    if (readAtById.size > 0) {
      queryClient.setQueryData<NotificationListResponseRuntime | undefined>(getListNotificationsQueryKey(), (current) => {
        if (!current) return current;
        let newlyRead = 0;
        const items = current.items.map((item) => {
          const readAt = readAtById.get(item.id);
          if (!readAt || item.readAt) return item;
          newlyRead += 1;
          return { ...item, readAt };
        });
        return { ...current, items, unreadCount: Math.max(0, current.unreadCount - newlyRead) };
      });
    }
  }, [queryClient, session.key]);

  const markAllNotificationsRead = useCallback(async () => {
    if (!session.key) return;
    const response = await requestMarkAllNotificationsRead();
    lastReadAllAt.current = response.readAt;
    claim.current?.controller.abort();
    claim.current = null;
    claimAgain.current = false;
    queryClient.setQueriesData<NotificationListResponseRuntime>(
      { predicate: (query) => query.queryKey[0] === "/api/notifications" },
      (current) => current ? {
        ...current,
        unreadCount: 0,
        items: current.items.map((item) => item.readAt ? item : { ...item, readAt: response.readAt }),
      } : current,
    );
    clearActivityToasts();
  }, [clearActivityToasts, queryClient, session.key]);

  const context = useMemo(
    () => ({ streamConnected, reportResultRendered, markAllNotificationsRead }),
    [markAllNotificationsRead, reportResultRendered, streamConnected],
  );
  return <ActivityContext.Provider value={context}>{children}</ActivityContext.Provider>;
}

function findNotificationIds(queryClient: ReturnType<typeof useQueryClient>, taskId: string): string[] {
  const data = queryClient.getQueryData<NotificationListResponseRuntime>(getListNotificationsQueryKey());
  return data?.items.filter((item) => item.taskId === taskId).map((item) => item.id) ?? [];
}

function httpStatus(error: unknown): number | null {
  if (!error || typeof error !== "object" || !("status" in error)) return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === "undefined" || document.visibilityState !== "hidden");
  useEffect(() => {
    const update = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  return visible;
}

export function useActivity(): ActivityContextValue {
  const value = useContext(ActivityContext);
  if (!value) throw new Error("useActivity must be used inside ActivityProvider");
  return value;
}

export function useActiveTaskPolling(taskId: string | null, enabled: boolean): void {
  const { streamConnected } = useActivity();
  const queryClient = useQueryClient();
  const visible = useDocumentVisible();
  useEffect(() => {
    if (!taskId || !enabled || !visible || streamConnected) return;
    const timer = window.setInterval(() => {
      void queryClient.refetchQueries({ queryKey: getGetAiTaskQueryKey(taskId), type: "active" }).catch(() => undefined);
    }, TASK_FALLBACK_MS);
    return () => window.clearInterval(timer);
  }, [enabled, queryClient, streamConnected, taskId, visible]);
}

export function useReportTaskResultRendered(taskId: string | null, rendered: boolean): void {
  const { reportResultRendered } = useActivity();
  useEffect(() => {
    if (!taskId || !rendered) return;
    void reportResultRendered({ taskId });
  }, [rendered, reportResultRendered, taskId]);
}
